import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin: one course's roster — reached from the admin course list.
// Add: pick a user → pick one of their active participants → enrolls
// directly as ACTIVE (manual, no payment). Pending seats can be activated;
// "remove" cancels the enrollment (nothing is deleted).

export function AdminCourseParticipantsPage() {
  const { t } = useTranslation()
  const { id } = useParams()
  const { user: me, loading: authLoading } = useAuth()

  const [course, setCourse] = useState(null)
  const [rows, setRows] = useState([])
  const [users, setUsers] = useState([])
  const [pickedUser, setPickedUser] = useState('')
  const [userParticipants, setUserParticipants] = useState([])
  const [pickedParticipant, setPickedParticipant] = useState('')
  const [payment, setPayment] = useState('plata')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  function reload() {
    api.admin.courses.participants(id)
      .then((d) => { setCourse(d.course); setRows(d.participants || []) })
      .catch((e) => setError(e.code || 'server'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (authLoading || !me || me.role !== 'admin') return
    reload()
    api.admin.users.list().then((d) => setUsers(d.users || [])).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, me, id])

  useEffect(() => {
    setUserParticipants([]); setPickedParticipant('')
    if (!pickedUser) return
    api.admin.users.get(pickedUser)
      .then((d) => setUserParticipants((d.participants || []).filter((p) => !p.deactivated_at)))
      .catch(() => {})
  }, [pickedUser])

  async function add(e) {
    e.preventDefault()
    if (!pickedParticipant) return
    setBusy(true); setError(null)
    try {
      const d = await api.admin.courses.addParticipant(id, {
        participant_id: Number(pickedParticipant),
        payment,
      })
      setRows(d.participants || [])
      setPickedParticipant('')
    } catch (err) {
      setError(err.code || 'server')
    } finally { setBusy(false) }
  }

  async function setStatus(row, status) {
    if (status === 'cancelled'
        && !confirm(t('admin_courses.roster_confirm_remove', { name: row.participant_name }))) return
    setBusy(true); setError(null)
    try {
      await api.admin.enrollments.setStatus(row.id, status)
      reload()
    } catch (err) {
      setError(err.code || 'server')
    } finally { setBusy(false) }
  }

  if (authLoading || loading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!me || me.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }
  if (!course) {
    return <main className="container">
      <p className="error">{t('admin_courses.errors.not_found')}</p>
      <p><Link to="/admin/courses">← {t('admin_courses.title')}</Link></p>
    </main>
  }

  return (
    <main className="container">
      <p><Link to="/admin/courses">← {t('admin_courses.title')}</Link></p>
      <h1>{t('admin_courses.roster_title', { name: course.name })}</h1>
      <p className="muted">
        {course.capacity !== null
          ? `${rows.length}/${course.capacity}`
          : rows.length}{' '}
        {t('admin_courses.roster_seats')}
      </p>

      {error && (
        <p className="error">
          {t(`admin_courses.errors.${error}`, {
            defaultValue: t(`courses.errors.${error}`, { defaultValue: error }),
          })}
        </p>
      )}

      <section className="card form-card">
        <h2>{t('admin_courses.roster_add_title')}</h2>
        <form onSubmit={add} noValidate>
          <label>
            <span>{t('admin_courses.roster_form_user')}</span>
            <select value={pickedUser} onChange={(e) => setPickedUser(e.target.value)}>
              <option value="">—</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
          </label>
          {pickedUser && (
            <label>
              <span>{t('admin_courses.roster_form_participant')}</span>
              <select value={pickedParticipant} onChange={(e) => setPickedParticipant(e.target.value)}>
                <option value="">—</option>
                {userParticipants.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}
          <label>
            <span>{t('admin_courses.roster_form_payment')}</span>
            <select value={payment} onChange={(e) => setPayment(e.target.value)}>
              <option value="plata">{t('admin_courses.payment_plata')}</option>
              <option value="fara_plata">{t('admin_courses.payment_fara_plata')}</option>
            </select>
            <span className="muted small">
              {payment === 'plata'
                ? t('admin_courses.payment_plata_hint')
                : t('admin_courses.payment_fara_plata_hint')}
            </span>
          </label>
          <div className="form-actions">
            <button type="submit" disabled={busy || !pickedParticipant}>
              {busy ? t('common.loading') : t('admin_courses.roster_add')}
            </button>
          </div>
        </form>
      </section>

      {rows.length === 0 && <p className="muted">{t('teacher.no_participants')}</p>}

      {rows.length > 0 && (
        <table className="results-table">
          <thead>
            <tr>
              <th>{t('teacher.col_participant')}</th>
              <th>{t('teacher.col_account')}</th>
              <th>{t('teacher.col_status')}</th>
              <th>{t('teacher.col_paid')}</th>
              <th>{t('admin_courses.col_actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  {r.participant_name}
                  {Number(r.billing_exempt) === 1 && (
                    <span className="badge muted-badge" style={{ marginLeft: '0.4rem' }}>
                      {t('admin_courses.payment_fara_plata')}
                    </span>
                  )}
                </td>
                <td>{r.account_name}<br /><span className="muted small">{r.account_email}</span></td>
                <td>{t(`enrollments.status_${r.status}`, { defaultValue: r.status })}</td>
                <td>{Number(r.paid) === 1 ? t('teacher.paid_yes') : t('teacher.paid_no')}</td>
                <td>
                  {r.status === 'pending_payment' && (
                    <button type="button" className="link-button" disabled={busy}
                            onClick={() => setStatus(r, 'active')}>
                      {t('admin_courses.roster_activate')}
                    </button>
                  )}
                  <button type="button" className="link-button danger" style={{ marginLeft: '0.5rem' }}
                          disabled={busy} onClick={() => setStatus(r, 'cancelled')}>
                    {t('admin_courses.roster_remove')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  )
}
